import dynamic from 'next/dynamic';
import { useMachine } from '@xstate/react';
import { useTranslation } from 'next-i18next';
import { getTenantById } from '@tenants';
import { useTenantId } from 'hooks/use-tenant-id';
import { useMyListingAgents } from 'hooks/use-my-listing-agents';
import { ConnectionRequestCtaCard } from 'ui/connection-request-form/connection-request-cta-card';
import { ConnectionRequestSuccessCard } from 'ui/connection-request-form/connection-request-success-card';
import { connectionRequestSidebarMachine } from 'domain/connection-request-form/sidebar-machine';
import { LOCALE_CONNECTION_REQUEST_PATHS, Namespaces } from 'types/locales';
import LoadingText from 'components/loading-text';
import type Listing from 'data/listing';

const MyListingAgentCard = dynamic(() => import('components/my-listing-agent-card'), {
  loading: () => <LoadingText />,
});

interface ConnectionRequestSidebarSectionProps {
  onBookShowing: (onFormSubmitted: () => void) => void;
  onRequestInfo: (onFormSubmitted: () => void) => void;
  listing: Listing;
}

/**
 * ConnectionRequestSidebarSection
 *
 * Section Layer: src/sections
 *
 * Owns the sidebar machine and renders either the CTA card or the success card
 * depending on whether the user has submitted a connection request.
 *
 * The sidebar machine transitions from `cta` → `submitted` when the form machine
 * signals a successful submission via the `onFormSubmitted` callback.
 */
export function ConnectionRequestSidebarSection({
  onBookShowing,
  onRequestInfo,
  listing,
}: ConnectionRequestSidebarSectionProps) {
  const { t } = useTranslation(Namespaces.CONNECTION_REQUEST);
  const tenantId = useTenantId();
  const tenant = getTenantById(tenantId);
  const phoneNumber = tenant.pvr.phoneNumber;
  const { agents, shouldShow: showAgentCard } = useMyListingAgents(listing);

  const [sidebarState, sendToSidebar] = useMachine(connectionRequestSidebarMachine);

  const onFormSubmitted = () => sendToSidebar({ type: 'SUBMITTED' });

  if (sidebarState.matches('submitted')) {
    return (
      <ConnectionRequestSuccessCard
        strings={{
          thanksLabel:   t(LOCALE_CONNECTION_REQUEST_PATHS.SUCCESS_MESSAGE_THANKS, { defaultValue: 'Thanks!' }),
          followUpLabel: t(LOCALE_CONNECTION_REQUEST_PATHS.SUCCESS_MESSAGE_WILL_FOLLOW_UP, { defaultValue: 'We will follow up with you.' }),
          callUsLabel: phoneNumber
            ? t(LOCALE_CONNECTION_REQUEST_PATHS.PHONE_CTA_CALL_US, { phoneNumber, defaultValue: `Call us ${phoneNumber}` })
            : undefined,
        }}
        phoneNumber={phoneNumber}
      />
    );
  }

  const hasAgentCard = showAgentCard && agents.length > 0;

  return (
    <ConnectionRequestCtaCard
      strings={{
        bookTourLabel:    t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_BUTTON_TAKE_TOUR, { defaultValue: 'Take a Tour' }),
        requestInfoLabel: t(LOCALE_CONNECTION_REQUEST_PATHS.FORM_BUTTON_REQUEST_INFO, { defaultValue: 'Request Info' }),
        tagline: '',
      }}
      onBookTour={() => onBookShowing(onFormSubmitted)}
      onRequestInfo={() => onRequestInfo(onFormSubmitted)}
      heading={hasAgentCard ? 'Contact eXp Realty Agent' : undefined}
      headerSlot={hasAgentCard ? <MyListingAgentCard agents={agents} /> : undefined}
    />
  );
}
